import { useMemo, useRef, useState } from "react";
import localitiesData from "@/data/israel-localities.json";
import { Check, ChevronDown, MapPin, Loader2, Locate } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

// Hebrew/Arabic normalization: strip niqqud + diacritics for fuzzy matching
function normalize(s) {
  if (!s) return "";
  return s
    .toString()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0591-\u05BD\u05BF\u05C1-\u05C7]/g, "") // Hebrew niqqud / cantillation
    .replace(/[\u064B-\u065F\u0670]/g, "") // Arabic harakat
    .replace(/['"`׳״()]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// Build in-memory indexes once
const LOCALITIES = localitiesData.map((c) => ({
  ...c,
  _search: normalize(`${c.he} ${c.en} ${c.ar || ""} ${c.region || ""}`),
}));
const ID_INDEX = new Map(LOCALITIES.map((c) => [c.id, c]));

export function getLocalityById(id) {
  if (id == null) return null;
  return ID_INDEX.get(Number(id)) || null;
}

export function localityDisplayName(loc, language = "en") {
  if (!loc) return "";
  if (language === "he" && loc.he) return loc.he;
  if (language === "ar" && loc.ar) return loc.ar;
  return loc.en || loc.he;
}

const TYPE_LABEL = {
  city: "City",
  town: "Town",
  rural: "Rural",
  tribe: "Tribe",
};
const TYPE_BG = {
  city: "#DCEFE3",
  town: "var(--te-input-bg)",
  rural: "#FFF1D6",
  tribe: "var(--te-danger-soft)",
};
const TYPE_FG = {
  city: "#1F5237",
  town: "var(--te-text-secondary)",
  rural: "#8B6310",
  tribe: "#DC2626",
};

function TypeBadge({ type }) {
  if (!type || !TYPE_LABEL[type]) return null;
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full shrink-0"
      style={{ background: TYPE_BG[type], color: TYPE_FG[type] }}
    >
      {TYPE_LABEL[type]}
    </span>
  );
}

function distanceKm(a, b) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLng = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

async function findNearestByCoords(lat, lng) {
  // 1) Match against bundled coords (major cities) within 25 km
  let best = null;
  let bestDist = Infinity;
  for (const c of LOCALITIES) {
    if (c.lat != null && c.lng != null) {
      const d = distanceKm([lat, lng], [c.lat, c.lng]);
      if (d < bestDist) { bestDist = d; best = c; }
    }
  }
  if (best && bestDist < 25) return best;

  // 2) OSM Nominatim reverse geocode → match by returned locality name
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&accept-language=he,en,ar`,
      { headers: { "Accept": "application/json" } }
    );
    const data = await res.json();
    const candidates = [
      data?.address?.city, data?.address?.town, data?.address?.village,
      data?.address?.municipality, data?.address?.county, data?.address?.suburb,
    ].filter(Boolean);
    for (const cand of candidates) {
      const n = normalize(cand);
      const found = LOCALITIES.find((c) =>
        normalize(c.en) === n || normalize(c.he) === n || normalize(c.ar) === n
      );
      if (found) return found;
    }
  } catch {
    // ignore
  }
  return best || null;
}

export function CitySelect({
  value,                 // numeric locality id (or null)
  onChange,              // (newId: number, locality: object) => void
  language = "en",
  placeholder = "Search city in Hebrew, Arabic, or English…",
  invalid = false,
  allowGeolocate = true,
  testId = "city-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [locating, setLocating] = useState(false);
  const triggerRef = useRef(null);

  const selected = useMemo(() => getLocalityById(value), [value]);

  const results = useMemo(() => {
    const q = normalize(query);
    if (!q) return LOCALITIES.slice(0, 50);
    const tokens = q.split(/\s+/).filter(Boolean);
    const matches = [];
    for (const c of LOCALITIES) {
      let score = 0;
      let ok = true;
      for (const t of tokens) {
        if (c._search.includes(t)) {
          score += c._search.startsWith(t) ? 3 : 1;
        } else { ok = false; break; }
      }
      if (ok) matches.push({ c, score });
      if (matches.length > 200) break;
    }
    matches.sort((a, b) => b.score - a.score);
    return matches.slice(0, 60).map((m) => m.c);
  }, [query]);

  const handleSelect = (c) => {
    onChange?.(c.id, c);
    setOpen(false);
    setQuery("");
  };

  const handleGeolocate = () => {
    if (!("geolocation" in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const found = await findNearestByCoords(pos.coords.latitude, pos.coords.longitude);
          if (found) handleSelect(found);
        } finally {
          setLocating(false);
        }
      },
      () => setLocating(false),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60_000 }
    );
  };

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            data-testid={testId}
            className={`te-input flex items-center justify-between gap-2 text-left w-full ${
              invalid ? "border-[#DC2626] focus:ring-[#DC2626]/40 focus:border-[#DC2626]" : ""
            }`}
            aria-expanded={open}
            aria-haspopup="listbox"
          >
            <span className="flex items-center gap-2 min-w-0">
              <MapPin size={16} className="text-[var(--te-text-muted)] shrink-0" />
              {selected ? (
                <span className="truncate text-[var(--te-text)]">
                  <span dir="auto">{localityDisplayName(selected, language)}</span>
                  {language !== "ar" && selected.ar && (
                    <span className="text-[var(--te-text-muted)] ml-2 text-xs" dir="rtl">{selected.ar}</span>
                  )}
                </span>
              ) : (
                <span className="text-[var(--te-text-muted)] truncate">{placeholder}</span>
              )}
            </span>
            <ChevronDown size={16} className="text-[var(--te-text-muted)] shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="p-0 w-[--radix-popover-trigger-width] min-w-[280px] max-w-[420px] border-[var(--te-divider)]"
        >
          <Command shouldFilter={false} className="rounded-xl">
            <CommandInput
              placeholder="Hebrew · Arabic · English"
              value={query}
              onValueChange={setQuery}
              data-testid={`${testId}-input`}
              dir="auto"
            />
            {allowGeolocate && (
              <button
                type="button"
                onClick={handleGeolocate}
                data-testid={`${testId}-detect-location`}
                className="w-full flex items-center gap-2 px-3 py-2.5 border-b border-[var(--te-input-bg)] text-sm text-[var(--te-primary)] hover:bg-[var(--te-input-bg)]/60 transition-colors"
              >
                {locating ? <Loader2 size={14} className="animate-spin" /> : <Locate size={14} />}
                <span className="font-medium">{locating ? "Locating you…" : "Use my current location"}</span>
              </button>
            )}
            <CommandList className="max-h-[300px]">
              {results.length === 0 ? (
                <div className="py-8 text-center text-sm text-[var(--te-text-muted)]" data-testid={`${testId}-empty`}>
                  No cities match.
                </div>
              ) : (
                <CommandGroup heading={query ? `${results.length} match${results.length === 1 ? "" : "es"}` : "Popular cities"}>
                  {results.map((c) => {
                    const isSelected = selected?.id === c.id;
                    return (
                      <CommandItem
                        key={c.id}
                        value={String(c.id)}
                        onSelect={() => handleSelect(c)}
                        data-testid={`${testId}-option-${c.id}`}
                        className="cursor-pointer flex items-start gap-2"
                      >
                        <Check size={14} className={`mt-1 shrink-0 ${isSelected ? "opacity-100 text-[var(--te-primary)]" : "opacity-0"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-sm font-medium text-[var(--te-text)]" dir="auto">
                            <span className="truncate">{c.en || c.he}</span>
                            {c.type && <TypeBadge type={c.type} />}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[var(--te-text-secondary)]">
                            {c.he && <span dir="rtl">{c.he}</span>}
                            {c.ar && <span dir="rtl" className="text-[var(--te-primary)]">{c.ar}</span>}
                            {c.region && <span dir="rtl" className="text-[var(--te-text-muted)]">· {c.region}</span>}
                            {c.council && c.type === "rural" && (
                              <span dir="rtl" className="text-[var(--te-text-muted)]">· {c.council}</span>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
